import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { PaymentMethod } from "@satyrsmc/shared/lib/enums";
import { formatPaymentMethod } from "@satyrsmc/shared/lib/formatters";
import { trpc } from "@/trpc";
import { Button } from "@satyrsmc/shared/components";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { EventDescriptionEditor } from "./EventDescriptionEditor";

/** Minimal shape the reminder dialog needs — deliberately narrower than any
 * one backend DTO so callers (currently just EventAttendeesPage's merged
 * attendees table) can adapt whatever attendee record they already have. */
interface ReminderTarget {
  id: string;
  displayName: string | null;
  paymentAmountCents: number | null;
  paymentMethod: PaymentMethod | null;
  contactEmail: string | null;
}

interface SendPaymentReminderDialogProps {
  rsvp: ReminderTarget;
  eventName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function defaultSubject(eventName?: string): string {
  return `Payment reminder — ${eventName ?? "your registration"}`;
}

/** How-to-pay copy, mirrored from the RSVP wizard's confirmation step
 * (Step4Confirmation.tsx) so admins and members see the same payment
 * instructions. */
function paymentInstructionsHtml(method: PaymentMethod | null, amount: string | null): string {
  const mailingAddress = `<p>Satyrs M/C<br>P.O. Box 1137<br>Los Angeles, CA 90078-1137</p>`;
  if (method === PaymentMethod.Check) {
    return `<p>Make checks payable to <strong>Satyrs M/C</strong> and mail to:</p>${mailingAddress}`;
  }
  if (method === PaymentMethod.Cash) {
    return `<p>Cash can be given to an officer in person, or mailed to:</p>${mailingAddress}`;
  }
  if (method === PaymentMethod.Zelle) {
    return `<p>Send${amount ? ` ${amount}` : ""} via Zelle to <strong>Treasurer@SatyrsMC.org</strong>.</p>`;
  }
  return "";
}

/** Default reminder body, seeded from what we already know about the
 * registration (amount owed, payment method, how to pay) — the admin can
 * edit freely before sending. */
function defaultBodyHtml(rsvp: ReminderTarget, eventName?: string): string {
  const name = rsvp.displayName ?? "there";
  const amount =
    rsvp.paymentAmountCents != null ? `$${(rsvp.paymentAmountCents / 100).toFixed(2)}` : null;
  const method = rsvp.paymentMethod ? formatPaymentMethod(rsvp.paymentMethod) : null;
  const amountPhrase =
    amount && method ? ` of ${amount} by ${method}` : amount ? ` of ${amount}` : "";
  const eventPhrase = eventName ? `for <strong>${eventName}</strong>` : "for your registration";

  const intro = `<p>Hi ${name},</p><p>This is a friendly reminder that your payment${amountPhrase} ${eventPhrase} is still pending. Please send payment at your earliest convenience.</p>`;
  const instructions = paymentInstructionsHtml(rsvp.paymentMethod, amount);
  const closing = `<p>Thanks!</p>`;

  return `${intro}${instructions}${closing}`;
}

export function SendPaymentReminderDialog({
  rsvp,
  eventName,
  open,
  onOpenChange,
}: SendPaymentReminderDialogProps) {
  const utils = trpc.useUtils();
  // The caller only mounts this dialog while a target rsvp is set (see
  // EventAttendeesPage), so a fresh mount is guaranteed per rsvp/open —
  // these lazy initializers are enough to reseed on every open.
  const [subject, setSubject] = useState(() => defaultSubject(eventName));
  const [bodyHtml, setBodyHtml] = useState(() => defaultBodyHtml(rsvp, eventName));
  const [replyToName, setReplyToName] = useState("");
  const [replyToEmail, setReplyToEmail] = useState("");

  const { data: adminDefault } = trpc.admin.rsvps.getReplyToDefault.useQuery(undefined, {
    enabled: open,
  });

  useEffect(() => {
    if (open && adminDefault) {
      setReplyToName(adminDefault.name);
      setReplyToEmail(adminDefault.email ?? "");
    }
  }, [open, adminDefault]);

  const displayName = rsvp.displayName ?? "this person";

  const sendReminder = trpc.admin.rsvps.sendPaymentReminder.useMutation({
    onSuccess: () => {
      toast.success(`Reminder sent to ${displayName}`);
      utils.admin.rsvps.getLogs.invalidate({ rsvpId: rsvp.id });
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const canSend =
    !sendReminder.isPending &&
    subject.trim().length > 0 &&
    bodyHtml.trim().length > 0 &&
    replyToName.trim().length > 0 &&
    replyToEmail.trim().length > 0;

  function handleSend() {
    sendReminder.mutate({
      rsvpId: rsvp.id,
      subject: subject.trim(),
      body_html: bodyHtml,
      reply_to_name: replyToName.trim(),
      reply_to_email: replyToEmail.trim(),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send Payment Reminder</DialogTitle>
          <DialogDescription>
            {rsvp.contactEmail ? `To: ${displayName} <${rsvp.contactEmail}>` : `To: ${displayName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Reply-to name</p>
              <Input value={replyToName} onChange={(e) => setReplyToName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Reply-to email</p>
              <Input
                type="email"
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium">Subject</p>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium">Message</p>
            <EventDescriptionEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              placeholder="Write your message..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!canSend} className="gap-1.5">
            {sendReminder.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Mail className="size-4" />
            )}
            {sendReminder.isPending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
